import map from "lodash/map";
import moment from "moment";
import { useCurrentActivity } from "PFApp/activities/show/hooks/use_current_activity";
import { KPICard } from "PFApp/components/kpi_card";
import { useKPIsReviewSidepanelActions } from "PFApp/components/kpis_review_sidepanel/use_kpis_review_sidepanel_actions";
import { Button } from "PFComponents/button";
import { Inline, Stack } from "PFComponents/containers";
import { LoadingDots } from "PFComponents/loading_dots";
import { Typography } from "PFComponents/typography";
import { useKPIRecalculations, useKPIResults } from "PFCore/hooks/queries/kpi";
import { KPIResult } from "PFCore/types/kpi";
import NoProfilesIllustration from "PFTheme/graphics/illustrations/illustration-no-profiles.svg";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import { ActivitySummary } from "../../activity_summary/activity_summary";
import css from "./activity_kpis.module.scss";

const getMaxCreatedAt = (results: KPIResult[]): string | null => {
  if (!results.length) {
    return null;
  }
  return results.reduce(
    (max, currentKPIResult) => (currentKPIResult.createdAt > max ? currentKPIResult.createdAt : max),
    results[0].createdAt
  );
};

export const ActivityKPIs = () => {
  const activity = useCurrentActivity();
  const { t } = useTranslation("activities", { keyPrefix: "show.kpi" });
  const { t: tTabs } = useTranslation("activities");
  const { openKPIsReviewSidepanel } = useKPIsReviewSidepanelActions();

  const { data: kpiResults, isFetching } = useKPIResults({ engagementId: activity.id });
  const { mutate: recalculate, isPending } = useKPIRecalculations(activity.id);

  const lastUpdatedAt = kpiResults?.length ? getMaxCreatedAt(kpiResults) : null;
  const lastUpdatedText = isPending
    ? t("recalculating")
    : lastUpdatedAt
      ? t("lastUpdated", { date: moment(lastUpdatedAt).fromNow() })
      : null;

  const handleReviewKPI = useCallback(
    (kpi: KPIResult) => {
      openKPIsReviewSidepanel({
        engagementId: activity.id,
        engagementName: activity.name,
        KPIs: kpiResults || [],
        initialKPIId: kpi.indicatorId
      });
    },
    [kpiResults, openKPIsReviewSidepanel, activity.id, activity.name]
  );

  return (
    <Inline
      gap="spacingXl"
      className={css.root}
      alignItems="start"
      role="tabpanel"
      aria-label={tTabs("show.tabs.kpi")}
      qaId="ActivityPage.kpisTab"
    >
      <ActivitySummary />

      <Stack as="section" gap="spacingLg" className={css.activityKpisRightSide}>
        {!!kpiResults?.length && (
          <Inline gap="spacingSm" alignItems="center" className={css.lastUpdated}>
            {lastUpdatedText && (
              <Typography variant="labelRegular" tag="span">
                {lastUpdatedText}
              </Typography>
            )}
            <Button
              icon="refresh"
              kind="secondary"
              disabled={isPending}
              onClick={() => recalculate()}
              aria-label={t("refreshAriaLabel")}
            />
          </Inline>
        )}
        {!kpiResults?.length && (
          <Stack className={css.emptyContainer} gap="spacing2xl" center>
            {!isFetching && (
              <>
                <NoProfilesIllustration />
                <Typography variant="h5" className={css.emptyText}>
                  {t("noKpisDefined")}
                </Typography>
              </>
            )}
            {isFetching && <LoadingDots circleSize="md" circlesEnabled />}
          </Stack>
        )}

        <Inline wrap="wrap" gap="spacingLg">
          {map(kpiResults, (kpiResult) => (
            <KPICard
              key={kpiResult.id}
              className={css.kpiItem}
              kpiResult={kpiResult}
              onClick={handleReviewKPI}
            />
          ))}
        </Inline>
      </Stack>
    </Inline>
  );
};
